// Offline maps catalog — search, download, delete, and storage summary
// for every region in REGIONS. Lives on /offline-control.

import { useEffect, useMemo, useState } from "react";
import {
  Search,
  CloudDownload,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Clock,
  Map as MapIcon,
  ChevronDown,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Capacitor } from "@capacitor/core";
import {
  listRegions,
  type RegionId,
  type RegionDescriptor,
  type DownloadProgress,
  type DownloadController,
  type RegionVerifyResult,
  downloadRegion,
  deleteRegion,
  getRegionStatus,
  getTotalRegionBytes,
  subscribeRegionChanges,
  verifyRegion,
} from "@/lib/offline-maps";
import {
  
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  StatusChip,
  type StatusChipVariant,
} from "@/components/roava";

function formatBytes(b: number): string {
  if (b <= 0) return "0 MB";
  const mb = b / (1024 * 1024);
  if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

type LocalState =
  | { kind: "idle" }
  | { kind: "downloading"; progress: DownloadProgress; controller: DownloadController }
  | { kind: "error"; message: string }
  | { kind: "deleting" };

export function OfflineMapsCatalog({ className = "" }: { className?: string }) {
  const regions = listRegions();
  const [query, setQuery] = useState("");
  const [, force] = useState(0);
  const isNative = Capacitor.isNativePlatform();

  // Bump on any registry change so storage summary + per-row status re-read.
  useEffect(() => {
    const unsub = subscribeRegionChanges(() => force((n) => n + 1));
    return () => { unsub(); };
  }, []);

  const totalBytes = getTotalRegionBytes();
  const downloadedCount = regions.filter(
    (r) => getRegionStatus(r.id).state === "present",
  ).length;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = q ? regions.filter((r) => r.label.toLowerCase().includes(q)) : regions;
    return [...rows].sort((a, b) => {
      const sa = getRegionStatus(a.id).state === "present" ? 0 : a.built ? 1 : 2;
      const sb = getRegionStatus(b.id).state === "present" ? 0 : b.built ? 1 : 2;
      if (sa !== sb) return sa - sb;
      return a.label.localeCompare(b.label);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, regions, downloadedCount]);

  return (
    <Collapsible className={`group rounded-2xl border border-[var(--roava-gray)] bg-[var(--roava-card)] ${className}`}>
      <CollapsibleTrigger className="flex w-full items-center gap-3 p-4 text-left">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[var(--roava-blue)]/10 text-[var(--roava-blue)]">
          <MapIcon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[var(--roava-text)]">Offline maps</div>
          <div className="text-xs text-[var(--roava-text-muted)] truncate">
            {!isNative
              ? "iOS app only"
              : `${downloadedCount} ${downloadedCount === 1 ? "map" : "maps"} · ${formatBytes(totalBytes)} on device`}
          </div>
        </div>
        <ChevronDown className="h-4 w-4 flex-shrink-0 text-[var(--roava-text-muted)] transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-[var(--roava-text-muted)]">
            Download regions over Wi-Fi so the map works in airplane mode and remote areas. Manage everything you've downloaded here.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <StatusChip variant={downloadedCount > 0 ? "success" : "neutral"}>
              {downloadedCount} {downloadedCount === 1 ? "map" : "maps"} · {formatBytes(totalBytes)} on device
            </StatusChip>
            {!isNative && (
              <StatusChip variant="warning">iOS app only</StatusChip>
            )}
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--roava-text-muted)]" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for a country or region…"
              className="w-full rounded-xl border border-[var(--roava-gray)] bg-[var(--roava-bg)] py-2.5 pl-9 pr-3 text-sm text-[var(--roava-text)] placeholder:text-[var(--roava-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--roava-blue)]/40"
            />
          </div>

          <div className="flex flex-col gap-2">
            {filtered.length === 0 && (
              <div className="rounded-xl bg-[var(--roava-gray)] p-3 text-sm text-[var(--roava-text-muted)]">
                No maps match "{query}".
              </div>
            )}
            {filtered.map((region) => (
              <RegionRow key={region.id} region={region} isNative={isNative} />
            ))}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function RegionRow({
  region,
  isNative,
}: {
  region: RegionDescriptor;
  isNative: boolean;
}) {
  const [status, setStatus] = useState(() => getRegionStatus(region.id));
  const [local, setLocal] = useState<LocalState>({ kind: "idle" });
  const [verify, setVerify] = useState<RegionVerifyResult | null>(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const unsub = subscribeRegionChanges((id: RegionId) => {
      if (id === region.id) {
        setStatus(getRegionStatus(region.id));
        setVerify(null);
      }
    });
    return () => { unsub(); };
  }, [region.id]);

  const downloaded = status.state === "present";
  const incomplete =
    downloaded &&
    status.state === "present" &&
    typeof status.expectedBytes === "number" &&
    status.bytes < status.expectedBytes;

  let chip: { variant: StatusChipVariant; label: string };
  if (!region.built) {
    chip = { variant: "neutral", label: "Coming soon" };
  } else if (incomplete) {
    chip = { variant: "warning", label: "Incomplete — re-download" };
  } else if (downloaded) {
    chip = { variant: "success", label: "Available offline" };
  } else if (local.kind === "downloading") {
    const pct = local.progress.percent;
    chip = { variant: "info", label: pct != null ? `Downloading ${Math.round(pct)}%` : "Downloading" };
  } else if (local.kind === "error") {
    chip = { variant: "warning", label: "Download failed" };
  } else {
    chip = { variant: "neutral", label: "Not downloaded" };
  }

  const start = () => {
    if (local.kind === "downloading" || !region.built) return;
    const controller = downloadRegion(region.id, (progress) => {
      setLocal((prev) => (prev.kind === "downloading" ? { ...prev, progress } : prev));
    });
    setLocal({
      kind: "downloading",
      progress: { bytes: 0, totalBytes: null, percent: null },
      controller,
    });
    controller.promise
      .then(() => {
        setLocal({ kind: "idle" });
        setStatus(getRegionStatus(region.id));
        setVerify(null);
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        setLocal({ kind: "error", message });
      });
  };

  const cancel = async () => {
    if (local.kind !== "downloading") return;
    try { await local.controller.cancel(); } finally { setLocal({ kind: "idle" }); }
  };

  const remove = async () => {
    setLocal({ kind: "deleting" });
    try {
      await deleteRegion(region.id);
      setStatus(getRegionStatus(region.id));
      setVerify(null);
    } finally {
      setLocal({ kind: "idle" });
    }
  };

  const runVerify = async () => {
    setVerifying(true);
    try {
      const result = await verifyRegion(region.id);
      setVerify(result);
      // Re-read status so a freshly-discovered expectedBytes shows up.
      setStatus(getRegionStatus(region.id));
    } finally {
      setVerifying(false);
    }
  };

  const reDownload = async () => {
    await deleteRegion(region.id);
    setStatus(getRegionStatus(region.id));
    setVerify(null);
    start();
  };


  return (
    <div className="rounded-xl border border-[var(--roava-gray)] bg-[var(--roava-bg)] p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="text-2xl leading-none mt-0.5" aria-hidden>{region.flag}</div>
          <div className="min-w-0">
            <div className="font-medium text-[var(--roava-text)] truncate">{region.label}</div>
            <div className="text-xs text-[var(--roava-text-muted)] mt-0.5">
              {downloaded && status.state === "present"
                ? `${formatBytes(status.bytes)} · saved ${formatDate(status.downloadedAt)}`
                : `~${region.approxMb} MB download`}
            </div>
          </div>
        </div>
        <StatusChip variant={chip.variant}>{chip.label}</StatusChip>
      </div>

      {local.kind === "downloading" && (
        <div className="mt-3">
          <div className="h-1.5 w-full rounded-full bg-[var(--roava-gray)] overflow-hidden">
            <div
              className="h-full bg-[var(--roava-blue)] transition-[width] duration-300"
              style={{ width: local.progress.percent != null ? `${local.progress.percent}%` : "10%" }}
            />
          </div>
        </div>
      )}

      {local.kind === "error" && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-[var(--roava-orange)]/10 p-2.5 text-xs text-[var(--roava-text)]">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-[var(--roava-orange)]" />
          <div className="flex-1 break-words">{local.message}</div>
        </div>
      )}

      {verify && (
        <div
          className={`mt-3 flex items-start gap-2 rounded-lg p-2.5 text-xs ${
            verify.ok
              ? "bg-[var(--roava-green)]/10 text-[var(--roava-text)]"
              : "bg-[var(--roava-orange)]/10 text-[var(--roava-text)]"
          }`}
        >
          {verify.ok ? (
            <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-[var(--roava-green)]" />
          ) : (
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-[var(--roava-orange)]" />
          )}
          <div className="flex-1 break-words">
            {verify.ok
              ? `File looks good · ${formatBytes(verify.bytes)}${verify.expectedBytes ? ` of ${formatBytes(verify.expectedBytes)}` : ""}.`
              : verify.reason}
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {!region.built && (
          <span className="inline-flex items-center gap-1.5 text-xs text-[var(--roava-text-muted)]">
            <Clock className="h-3.5 w-3.5" />
            Map file not built yet
          </span>
        )}
        {region.built && !downloaded && local.kind !== "downloading" && (
          <PrimaryButton onClick={start} disabled={!isNative || local.kind === "deleting"}>
            <CloudDownload className="h-4 w-4 mr-2" />
            {local.kind === "error" ? "Try again" : "Download"}
          </PrimaryButton>
        )}
        {local.kind === "downloading" && (
          <SecondaryButton onClick={cancel}>Cancel</SecondaryButton>
        )}
        {downloaded && incomplete && local.kind !== "downloading" && (
          <PrimaryButton onClick={reDownload} disabled={!isNative}>
            <CloudDownload className="h-4 w-4 mr-2" />
            Re-download
          </PrimaryButton>
        )}
        {downloaded && local.kind !== "downloading" && (
          <DangerButton onClick={remove} disabled={local.kind === "deleting"}>
            {local.kind === "deleting" ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Delete
          </DangerButton>
        )}
        {downloaded && local.kind !== "downloading" && (
          <button
            type="button"
            onClick={runVerify}
            disabled={verifying || !isNative}
            className="text-xs text-[var(--roava-blue)] underline-offset-2 hover:underline disabled:opacity-50"
          >
            {verifying ? "Verifying…" : "Verify"}
          </button>
        )}
        {downloaded && !incomplete && local.kind === "idle" && region.id === "mongolia" && (
          <span className="inline-flex items-center gap-1.5 text-xs text-[var(--roava-text-muted)]">
            <CheckCircle2 className="h-3.5 w-3.5 text-[var(--roava-green)]" />
            In use by the trip map
          </span>
        )}
      </div>
    </div>
  );
}

